import { titleCase } from "title-case";
import { broadcast } from "../core/hooks/useChannel";
import { DataState, ValidationContext } from "../core/hooks/useData";
import { isStr, isValidDate, toBool, toDate, toInt, toNumber, toStr, tomorrow } from "../core/utils/conversion";
import { Address, Company, Name } from "../types";
import { ContainerMessages, ContainerState, Element, EventContext, FieldMessages, PresenterState, SubFormElement } from "./types";
import { getFirstObjectValue, getObjectValue } from "../core/utils/objectPath";
import { currencyToWords, formatDate, formatDatabaseDate, numberToWords } from "rant-utils";
import { format } from "path";
import { RangeOptions } from "../core/form/validators";
import { settings } from "../settings";
import { createScriptEngine, CustomScriptFunctions } from "../core/utils/script";
import { companyName, dateValue, defaultFormatAddress, entityAddress, entityName, formatAddress, formatDateCustom, formatName, formatPOB, formatter, individualName, trustName } from "../formatters/format";



const { 
	get,
	getCount,
	getCountsOf,
	...fnsToKeep 
} = CustomScriptFunctions;


export const NameCustomScriptFunctions = {

	individualName,

	companyName,

	trustName,

	entityName,

	formatName,

}

export const AddressCustomScriptFunctions = {

	defaultFormatAddress,

	formatAddress,

	entityAddress,

}

export const BirthCustomScriptFunctions = {

	formatPOB,

}

export const DateCustomScriptFunctions = {

	formatDateCustom,
	formatDatabaseDate,
}

export const FormatCustomScriptFunctions = {

	formatter,

}

export const ASICCustomScriptFunctions = {

	mapDataForASIC_Logic: (
		formType: string,
		utils: {
			presenter: PresenterState,
			get: (prop: string) => any,
			getWithin: (containerID: string, prop: string) => any,
			refLookupValue: (prop: string, refTable: string, compareWithColName?: string, returnColName?: string) => any,
		}
	) => {
		//console.log("======> mapDataForASIC: " + JSON.stringify(awf));
	
		const {
			presenter,
			get, refLookupValue, getWithin,
		} = utils;
	
		function addr(obj: any) {
			if (!obj) return undefined;
	
			const isAus = (obj && obj.country && obj.country.toLowerCase() === "australia");
	
			const result = {
				careOf: obj.careOf,
				line2: obj.line2,
				street: obj.street,
				locality: obj.locality,
				state: obj.state,
				postCode: obj.postCode,
				country: isAus ? "" : obj.country,
			};
			return result;
		}
		function name(obj: any) {
			if (!obj) return undefined;
	
			const result = {
				familyName: obj.family,
				givenName1:obj.given1,
				givenName2:obj.given2,
				givenName3:obj.given3,
			};
			return result;
		}
	
		function getHolder(entity: any) {
			let indName, orgName, acn, address;
			if (entity.entityType === "individual") {
				indName = name(entity.individual.name);
				address = addr(entity.individual.address);
	
			} else if (entity.entityType === "company") {
				orgName = entity.company.name;
				acn = entity.company.acn;
				address = addr(entity.company.address);
	
			} else if (entity.entityType === "trust") {
				// use the trustee as the holder
				if (entity.trust.trusteeType === "individual") {
					indName = name(entity.trust.trustee.name);
				} else {
					orgName = entity.trust.trusteeCompany.name;
					acn = entity.trust.trusteeCompany.acn;
				}
				address = addr(entity.trust.address);
			}
			
			return {
				indName, orgName, acn, address,
			};
		}
	
		let data;
	
		if (formType === "RA51") {
	
			data = {
				formType: "RA51",
				companyDetails: {
					companyName: get("companyName"),
					acn: get("acn"),
				},
			};
	
		} else if (formType === "201") {
			const isACNUsedForCompanyName = get("useACN");
			const useExistingABN = get("useExistingABN");
			const isBefore2012 = get("businessNameDetails.isBefore2012");
	
			const occupyRegisteredOffice = get("occupyRegisteredOffice");
	
			const principalAddressSame = get("principalAddressSame");
	
			const isUltimateHolding = get("isUltimateHolding");
	
			let businessNamesOwned;
			if (useExistingABN && isBefore2012) {
				businessNamesOwned = [];
				for(let state of ["ACT","NSW","NT","QLD","SA","TAS","VIC","WA"]) {
					const num = get("businessNameDetails.before2012Details.existing" + state);
					if (num) {
						businessNamesOwned.push(
							{
								placeOfRegistrationOfBusinessName: state,
								registrationNumberOfBusinessName: num,
							}
						);
					}
				}
			}
	
			const officers = [];
			const parties = get("parties");
			for(let p of parties) {
				if (p.entity.entityType !== "individual") continue;
	
				const individual = p.entity.individual;
				const pob = individual.pob;
	
				const officer = {
					personalDetails: {
						name: name(individual.name),
						birthDetails: {
							date: individual.dob,
							locality: pob ? ((pob.country && pob.country.toLowerCase() === "australia") ? pob.suburb : pob.locality) : undefined,
							stateAndOrCountry: pob ? ((pob.country && pob.country.toLowerCase() === "australia") ? pob.state : pob.country) : undefined,
						},
						address: addr(individual.address),
					},
					officesHeld: [] as { office: string }[],
				};
				officers.push(officer);
				if (p.entity.director) officer.officesHeld.push({ office: "DIR" });
				if (p.entity.secretary) officer.officesHeld.push({ office: "SEC" });
			}
	
			const shareClasses = [];
			const shareMembers = [];
			const shares = get("shares");
			const uniqueClasses = {} as any;
			for(let s of shares) {
	
				// ensure correct data types
				s.shareCount = toInt(s.shareCount);
				s.isFullyPaid = toBool(s.isFullyPaid);
				s.shareOwing = toNumber(s.shareOwing);
				s.shareCost = toNumber(s.shareCost);
				s.isBeneficiallyHeld = toBool(s.isBeneficiallyHeld);
	
				// classes
				let cls = uniqueClasses[s.shareClass];
				if (!cls) {
					cls = {
						shareClass: s.shareClass, 
						fullTitle: 
							s.isShareTitleNonStandard ? s.shareClassCustomName
							: refLookupValue(s.shareClass, "share-classes", "Code", "Name"),
						totalNumberIssued: 0,
						totalAmountPaid: 0,
						totalAmountUnpaid: 0,
					};
					uniqueClasses[s.shareClass] = cls;
				}
	
				cls.totalNumberIssued += s.shareCount;
				const unpaid = s.isFullyPaid ? 0 : (s.shareCount * s.shareOwing);
				const paid = (s.shareCost * s.shareCount) - unpaid;
				cls.totalAmountUnpaid += unpaid;
				cls.totalAmountPaid += paid;
	
				// members
				if (!s.holders || s.holders.length < 0) continue;
				// find related party
				const firstHolder = s.holders[0];
				const entity = getWithin(firstHolder, "entity");
				if (!entity) continue;
	
				const mainHolder = getHolder(entity);
	
				// joint holders
				const jointHolders = [];
				for(let i=1;i<s.holders.length;i++) {
					const entity = getWithin(s.holders[i], "entity");
					if (!entity) continue;
	
					jointHolders.push(getHolder(entity));
				}
	
				const member = {
					holding: {
						shareClass: s.shareClass,
						numberTakenUp: s.shareCount,
						areSharesFullyPaid: s.isFullyPaid,
						isMemberBeneficialOwner: s.isBeneficiallyHeld,
						totalAmountPaid: paid,
						totalAmountUnpaid: unpaid,
						amountPaidPerShare: s.isFullyPaid ? s.shareCost : (s.shareCost - s.shareOwing),
						amountUnpaidPerShare: s.isFullyPaid ? 0 : s.shareOwing,
					},
	
					member: {
						name: mainHolder.indName,
						organisationName: mainHolder.orgName,
						acn: mainHolder.acn,
						address: mainHolder.address,
						doesHaveACNorARBN: (mainHolder.acn && mainHolder.acn.length > 0),
					},
	
					memberJointHolders: jointHolders.map((jh) => {
						return {
							name: jh.indName,
							organisationName: jh.orgName,
							acn: jh.acn,
							address: jh.address,
							doesHaveACNorARBN: (jh.acn && jh.acn.length > 0),
						};
					}),
				
				};
				shareMembers.push(member);
	
			
	
			}
			for(let m in uniqueClasses) {
				shareClasses.push(uniqueClasses[m]);
			}
	
			const manualReviewText = get("manualReview");
	
			data = {
				formType: "201",
				
				companyDetails: {
					companyName: !isACNUsedForCompanyName ? get("companyNameFull") : undefined,
					
					companyType: "APTY",
					companyClass: "LMSH",
					companySubClass: "PROP",
	
					isACNUsedForCompanyName,
					legalElementsForACN: isACNUsedForCompanyName ? get("legalElement") : undefined,
	
					isProposedNameIdenticalToRegisteredBusinessName: useExistingABN,
	
					jurisdiction: get("stateRegistered"),
	
					abn: (useExistingABN && !isBefore2012) ? get("businessNameDetails.after2012Details.abn") : undefined,
				},
	
				// nameReservation: {
				// 	applicantIfPerson: ,
				// 	applicantIfOrganisation: ,
				// 	reservationDocumentNumber: ,
				// },
	
				businessNamesOwned,
	
				//membersGuarantee,
	
				registeredOffice: {
					registeredOfficeAddress: addr(get("regOffice")),
					willCompanyOccupyAddress: occupyRegisteredOffice,
					nameOfOccupier: !occupyRegisteredOffice ? get("occupierName") : undefined,
					isOccupierConsentObtained: !occupyRegisteredOffice ? true : undefined,
				},
	
				//standardHours,
	
				principalPlaceOfBusiness: {
					principalPlaceOfBusinessAddress: principalAddressSame ? addr(get("regOffice")) : addr(get("ppob")),
				},
	
				ultimateHoldingCompany: isUltimateHolding ? {
					placeOfIncorporation: get("ultimateHolding.country"),
					companyName: get("ultimateHolding.name"),
					acnOrArbn: get("ultimateHolding.acn") ? get("ultimateHolding.acn") : get("ultimateHolding.cn"),
				} : undefined,
	
				officers,
	
				shareClasses,
	
				shareMembers,
	
				//nonShareMembers,
	
				manualReviewText: {
					text: manualReviewText,
				},
	
				administrativeDetails: {
					requestManualReview: (manualReviewText && manualReviewText.length > 0),
				},
			
				applicant: {
					name: {
						givenName1: "Joel",
						familyName: "Edwards",
					},
					address: {
						street: "1 Collins Street",
						locality: "Melbourne",
						state: "VIC",
						postCode: "3000",
					}		
				},
	
				applicantSignature: {
					signatoryName: {
						givenName1: "Joel",
						familyName: "Edwards",
					},
					//signatoryRole: "AGT", // don't supply if applicant is person
				},
	
				
			};
	
		}
	
		if (data) {
			presenter.setGeneratedData("asic-form", data);
		}
	
	},

}

export const InvoiceCustomScriptFunctions = {

	constructInvoice: (props: {
		title: string,
		items: any[],
	}) => {
		const inv = {
			id: "DRAFT_INVOICE",
			title: props.title,
			tax: 0,
			subTotal: 0,
			disbursements: 0,
			total: 0,
			items: [] as any[],
		};
	
		// NOTE: invoice items may have conditions which exclude them 
		let disbursements = 0; // all of the non-gst items
		for(let item of props.items) {
			if (item.active !== false) {
				inv.items.push(item);
	
				inv.tax += (item.tax * item.qty);

				// sub total only the items that have tax
				if (item.tax > 0) {
					inv.subTotal += (item.unitCost * item.qty);
				} else {
					inv.disbursements += (item.unitCost * item.qty);
				}
			}
		}
	
		inv.total = inv.tax + inv.subTotal + inv.disbursements;
	
		return inv;
	},

}

export const DataEntryCustomScriptFunctions = {

	...fnsToKeep,

	...NameCustomScriptFunctions,
	...AddressCustomScriptFunctions,
	...BirthCustomScriptFunctions,
	...DateCustomScriptFunctions,
	...FormatCustomScriptFunctions,
	...ASICCustomScriptFunctions,
	...InvoiceCustomScriptFunctions,

};


export function createDataEntryScriptEngine(presenter: PresenterState) {


	return createScriptEngine({
		customFunctions: {
			...DataEntryCustomScriptFunctions,

			// function get(prop: string) { 
			// 	// if (prop === "entity.display") {
			// 	// 	debugger;
			// 	// }
			// 	return presenter.getValue(prop, getNamedScopeItem("data"), undefined, namedScopeObjects);
			// }


			setTitle: (title: string) => {
				presenter.setTitle(title);
			},
		
		},

		onCreateScopeDependentFunctions: (scope) => {

			function get(prop: string) { 
				// if (prop === "entity.display") {
				// 	debugger;
				// }
				if (!scope.container.data) {
					debugger;
				}
				return presenter.getValue(
					prop,
					{
						dataState: scope.container.data,
						scope,
					},
				);
			}

			function getCountsOf(arrayProp: string, props: string[]) {
				//debugger;
				
				const arr = get(arrayProp);

				const counts = {} as any;
				for(let p of props) {
					counts[p] = 0;
				}

				if (Array.isArray(arr)) {
					for(let a of arr) {
						for(let p of props) {
							const condition = getFirstObjectValue(a, p);
							if (condition) counts[p]++;
						}
					}
				}

				return counts;
			}			

			function getWithin(containerID: string, prop: string) {
				// fetch the data context for the given container instance id
				const msg = {
					type: ContainerMessages.GET_REF,
					data: undefined,
				};
				broadcast(containerID, msg);
				if (msg.data) {
					// now fetch value from that context
					return presenter.getValue(prop, {
						dataState: msg.data,
						scope,
					});
				} else {
					return undefined;
				}
			}

			function set(prop: string, value: any) {
				// if (prop === "display") {
				// 	debugger;
				// }
				presenter.setValue(prop, value, scope.container.data);
			}

			function refLookupValue(val: string, refTable: string, compareWithColName?: string, returnColName?: string) {
				const ref = presenter.getRefData(refTable);
				if (!ref) return "";

				// TODO: prob need options for choosing column (or maybe even specifying the field def to source display column from)

				if (!compareWithColName) compareWithColName = "ID";
				if (!returnColName) returnColName = "Name";

				let compareWithCol, returnCol;
				for(let c of ref.columns) {
					if (c.title === compareWithColName) compareWithCol = c.id;
					if (c.title === returnColName) returnCol = c.id;
				}

				for(let r of ref.rows) {
					if (r[compareWithCol] === val) return r[returnCol];
				}

				return "";
			}


			return {
				get,
				getWithin,
				getCountsOf,
				set,
				refLookupValue,

				getStr: (prop: string) => {
					const val = get(prop);
					if (isStr(val)) return val;
					return "";
				},

				setDate: (prop: string, dt?: Date) => {
					const value = dateValue(dt ? dt : new Date());
					set(prop, value);
				},

				getCount: (prop: string) => {
					const v = get(prop);
					if (Array.isArray(v)) {
						return v.length;
					} else {
						return 0;
					}
				},

				setVisible: (prop: string, value: any) => {
					const visible = toBool(value);

					const f = scope.container.find(prop);
					if (f) {
						broadcast(f.channelID, {
							type: FieldMessages.SET_VISIBILITY,
							visible,
						});
					}
				},

				dob: (prop: string) => {
					const ent = get(prop);
					if (!ent) return;

					return ent.individual.dob;
				},

				birthDetails: (prop: string) => {
					const ent = get(prop);
					if (!ent || ent.entityType !== "individual") return;

					const individual = ent.individual;

					let result = individual.dob ? formatDate(individual.dob) : "Not entered";

					if (individual.pob && individual.pob.suburb) {
						const pob = individual.pob;
						if (pob.region && pob.region.toLowerCase() === "australia") {
							result += "\n" + pob.suburb + " " + (pob.state ? pob.state : "");
						} else {
							result += "\n" + pob.suburb + " " + (pob.country ? pob.country : "");
						}
					}
					return result;
				},

				refLookup: (prop: string, refTable: string, compareWithColName?: string, returnColName?: string) => {
					const val = get(prop);
					return refLookupValue(val, refTable, compareWithColName, returnColName);
				},


				mapDataForASIC: (formType: string) => {
					return DataEntryCustomScriptFunctions.mapDataForASIC_Logic(formType, {
						presenter,
						get,
						getWithin,
						refLookupValue,
					});
				},
				
				matchAllocationsToShareholders: (sharesProp: string, partiesProp: string) => {
					const parties = get(partiesProp);
					if (!parties) return;

					const shares = get(sharesProp);
					if (!shares) return;

					for(let p of parties) {
						const ent = getFirstObjectValue(p, "entity");
						if (!ent) continue;
						if (!ent.shareholder) continue;
						
						let found = false;
						for(let s of shares) {
							if (!s.holders) continue;

							if (s.holders.indexOf(p.containerID) >= 0) {
								found = true; 
								break;
							}
						}

						if (!found) return false;
					}

					return true;
				}				


			}
		},

	});

}


// const scriptEngine = createScriptEngine({
// 	customFunctions: {
// 		...DataEntryCustomScriptFunctions,

		
// 	},
// });

// export function runScript2(script: string, scope: any, additionalScopeItems?: (ScopeObject | Object)[]) {
// 	return scriptEngine.run(script, scope, additionalScopeItems);
// }



// export function _ORIGINAL_runScript(script: string, context: EventContext, ...restArgs: any) {
	
// 	//debugger;

// 	// options can be supplied globally from the presenter
// 	// or a subform could have its own options 
// 	const options = {
// 		...((context && context.presenter) ? context.presenter.options : {}),
// 		...context.options,
// 	};

// 	const { presenter, container } = context;




// 	const scope: ScopeObject[] = [];
// 	const _additional = [] as any[];
// 	const namedScopeObjects: ScopeObject[] = [];

// 	if (context.scope) {
// 		for(let s of context.scope) {
// 			if (s.name) {
// 				// can use named as provided
// 				namedScopeObjects.push(s);

// 			} else {
// 				// have to give a name through additional[] so it can be referenced
// 				_additional.push(s.obj);
// 				scope.push({
// 					name: `defaultScope._additional[${_additional.length-1}]`,
// 					obj: s.obj,
// 				});	
// 			}
// 		}
// 	}








// 	function getNamedScopeItem(name: string) {
// 		for(let s of namedScopeObjects) {
// 			if (s.name == name) return s.obj;
// 		}
// 		return undefined;
// 	}









// 	/* eslint-disable */
// 	// return the value for a field
// 	function get(prop: string) { 
// 		// if (prop === "entity.display") {
// 		// 	debugger;
// 		// }
// 		return presenter.getValue(prop, getNamedScopeItem("data"), undefined, namedScopeObjects);
// 	}
// 	function getWithin(containerID: string, prop: string) { 
// 		// fetch the data context for the given container instance id
// 		const msg = {
// 			type: ContainerMessages.GET_REF,
// 			data: undefined,
// 		};
// 		broadcast(containerID, msg);
// 		if (msg.data) {
// 			// now fetch value from that context
// 			return presenter.getValue(prop, msg.data, undefined, namedScopeObjects);
// 		} else {
// 			return undefined;
// 		}
// 	}
// 	function getStr(prop: string) {
// 		const val = get(prop);
// 		if (isStr(val)) return val;
// 		return "";
// 	}
// 	function set(prop: string, value: any) {
// 		presenter.setValue(prop, value, getNamedScopeItem("data"));
// 	}
// 	function setDate(prop: string, dt?: Date) {
// 		const value = dateValue(dt ? dt : new Date());
// 		set(prop, value);
// 	}
// 	function getCount(prop: string) {
// 		const v = presenter.getValue(prop, getNamedScopeItem("data"), undefined, namedScopeObjects);
// 		if (Array.isArray(v)) {
// 			return v.length;
// 		} else {
// 			return 0;
// 		}
// 	}
// 	function setTitle(title: string) {
// 		presenter.setTitle(title);
// 	}

// 	function setVisible(prop: string, value: any) {
// 		const visible = toBool(value);

// 		const f = container.find(prop);
// 		if (f) {
// 			broadcast(f.channelID, {
// 				type: FieldMessages.SET_VISIBILITY,
// 				visible,
// 			});
// 		}
// 	}

// 	function dob(prop: string) {
// 		const ent = get(prop);
// 		if (!ent) return;

// 		return ent.individual.dob;
// 	}

// 	function birthDetails(prop: string) {
// 		const ent = get(prop);
// 		if (!ent || ent.entityType !== "individual") return;

// 		const individual = ent.individual;

// 		let result = individual.dob ? formatDate(individual.dob) : "Not entered";

// 		if (individual.pob && individual.pob.suburb) {
// 			const pob = individual.pob;
// 			if (pob.region && pob.region.toLowerCase() === "australia") {
// 				result += "\n" + pob.suburb + " " + (pob.state ? pob.state : "");
// 			} else {
// 				result += "\n" + pob.suburb + " " + (pob.country ? pob.country : "");
// 			}
// 		}
// 		return result;
// 	}

// 	function refLookup(prop: string, refTable: string, compareWithColName?: string, returnColName?: string) {
// 		const val = get(prop);
// 		return refLookupValue(val, refTable, compareWithColName, returnColName);
// 	}

// 	function refLookupValue(val: string, refTable: string, compareWithColName?: string, returnColName?: string) {
// 		const ref = presenter.getRefData(refTable);
// 		if (!ref) return "";

// 		// TODO: prob need options for choosing column (or maybe even specifying the field def to source display column from)

// 		if (!compareWithColName) compareWithColName = "ID";
// 		if (!returnColName) returnColName = "Name";

// 		let compareWithCol, returnCol;
// 		for(let c of ref.columns) {
// 			if (c.title === compareWithColName) compareWithCol = c.id;
// 			if (c.title === returnColName) returnCol = c.id;
// 		}

// 		for(let r of ref.rows) {
// 			if (r[compareWithCol] === val) return r[returnCol];
// 		}

// 		return "";
// 	}

// 	function mapDataForASIC(formType: string) {
// 		return mapDataForASIC_Logic(formType, {
// 			presenter,
// 			get,
// 			getWithin,
// 			refLookupValue,
// 		});
// 	}
	
// 	function matchAllocationsToShareholders(sharesProp: string, partiesProp: string) {
// 		debugger;

// 		const parties = get(partiesProp);
// 		if (!parties) return;

// 		const shares = get(sharesProp);
// 		if (!shares) return;

// 		for(let p of parties) {
// 			const ent = getFirstObjectValue(p, "entity");
// 			if (!ent) continue;
// 			if (!ent.shareholder) continue;
			
// 			let found = false;
// 			for(let s of shares) {
// 				if (s.holders.indexOf(p.containerID) >= 0) {
// 					found = true; 
// 					break;
// 				}
// 			}

// 			if (!found) return false;
// 		}

// 		return true;
// 	}



// 	const defaultScope = {
// 		// ...getDefaultScriptScope({
// 		// 	scope,
// 		// 	_additional,
// 		// 	namedScopeObjects,
// 		// }),
// 		...getEntityNameFunctions(get),
// 		...getAddressFunctions(get),
// 		formatter,

// 		get, // override

// 		_additional,
// 	};

// 	scope.splice(0, 0, {
// 		name: "defaultScope",
// 		obj: defaultScope
// 	});

// 	for(let i=0;i<restArgs.length;i++) {
// 		const a = restArgs[i];
// 		defaultScope._additional.push(a);
// 		scope.push({
// 			name: `defaultScope._additional[${i}]`,
// 			obj: a,
// 		});
// 	}



// 	let result;
// 	let s;
// 	try {
// 		s = prepareScript(script, scope, namedScopeObjects);
// 		result = eval(s);
// 	} catch (e) {
// 		console.error("ERROR running script: " + e);
// 		debugger;
// 	}
// 	return result;
// }


interface CustomOptions {
	script: string,
	message: string,
}

export function customRule(
	context: ValidationContext<any>,
	prop: string,
	options: CustomOptions,
	presenter: PresenterState,
	container: ContainerState,
	def: Element) 
{
	const eventContext: EventContext = {
		container,
		presenter: presenter, 
		type: "validate",
		def,
		dataWindows: [],
		debugContext: [
			"customRule",
			prop,
		],
	};

	const addError = (message: string) => {
		context.errors.push({ message, prop, display: true });
	}

	const result = presenter.scriptEngine.run(options.script, eventContext);
	if (!result) {
		if (options.message) {
			addError(options.message);
		}	
	}
}

interface OccurrenceRangeOptions extends RangeOptions {
	list: string,
	prop: string,
}

export function occurrenceRangeRule(context: ValidationContext<any>, prop: string, options: OccurrenceRangeOptions, presenter: PresenterState) {
	let arr;
	if (options.list) arr = presenter.getValue(options.list, { dataState: context.data });
	let count = 0;

	if (arr) {
		if (!options.prop) {
			count = arr.length;
		} else {
			for(let a of arr) {
				const v = getObjectValue(a, options.prop);
				if (v) count++;
			}				
		}
	}

	if (arr && Array.isArray(arr))  {
		const min = toNumber(options.min);
		const max = toNumber(options.max);
		const hasMin = !isNaN(min);
		const hasMax = !isNaN(max);
	
		const val = count;
	
		const validMin = (hasMin && (val >= min)) || !hasMin;
		const validMax = (hasMax && (val <= max)) || !hasMax;
	
		if (hasMin && hasMax && (val > max || val < min)) {
			context.errors.push({ message: "Must be between " + min + " and " + max, prop, display: true });
		} else {
			if (!validMin) {
				if (options.minMessage) {
					context.errors.push({ message: options.minMessage, prop, display: true });
				} else {
					context.errors.push({ message: "Must be at least " + min, prop, display: true });
				}
			}
			if (!validMax) {
				if (options.maxMessage) {
					context.errors.push({ message: options.maxMessage, prop, display: true });
				} else {
					context.errors.push({ message: "Must be no greater than " + max, prop, display: true });
				}
			}
		}
	}
}

interface AddressRuleOptions {
	require?: {
		street?: boolean,
		locality?: boolean,
		state?: boolean,
		postCode?: boolean,
		country?: boolean,
	},

	noPOBox?: boolean,

	allowInternational?: boolean,
}

export function addressRule(context: ValidationContext<any>, prop: string, options: AddressRuleOptions) {

	const { data, errors } = context;

	function hasVal(prop: string) {
		const v = addr[prop];
		return (v && v.length > 0);
	}

	var addr = data.getValue(prop);
	if (!addr) {
		// as a fallback try and just use the data context itself
		addr = data.value;
	}

	if (options.require) {

		const r = options.require;

		var failed = false;

		if (r.street && (!hasVal("street") && !hasVal("line2"))) failed = true;
		if (r.locality && !hasVal("locality")) failed = true;
		if (r.state && !hasVal("state")) failed = true;
		if (r.country && !hasVal("country")) failed = true;

		if (failed) {
			errors.push({ message: "Required", prop });
		}
	}

	if (options.noPOBox && (addr.line2 || addr.street)) {
		// check the line2 & street together
		const val = (addr.line2 + addr.street).toUpperCase();

		var check = ["PO BOX", "P.O", "GPO ", "G.P.O", "POBOX"];
		for (var i = 0; i < check.length; i++) {
			if (val.indexOf(check[i]) >= 0) {
				errors.push({ message: "Cannot be a PO Box", prop, display: true });
				break;
			}
		}
	}

	if (!options.allowInternational) {
		if (!addr.country || addr.country.toUpperCase() !== settings.country.default) {
			errors.push({ message: `Must be an address within ${settings.country.default}`, display: true });
		}
	}
	
};